/**
 * Connector ids and union helpers.
 *
 * Prefer requiredConnectors from the backend (catalog / register). When a row
 * has none (suite cards, invented metrics), fall back to family/id rules so
 * Gate 2 still asks only for the systems those metrics need.
 */

import {
  metricNeedsAskApi,
  metricNeedsRetrievedContext,
} from "../pages/gate2/implementedRagMetrics";

export const CONNECTORS = Object.freeze({
  VECTOR_STORE: "vector_store",
  REST_API: "rest_api",
  TRACES: "traces",
});

const CONNECTOR_ORDER = [
  CONNECTORS.VECTOR_STORE,
  CONNECTORS.REST_API,
  CONNECTORS.TRACES,
];

const NO_CONNECTOR_IDS = new Set(["am_tevv_coverage", "cp_tevv_coverage_index"]);

/** Agent metrics that need a tool list on the API response or LangSmith spans. */
export const TOOL_SPAN_METRIC_IDS = new Set([
  "am_tool_call_success_rate",
  "am_tool_call_explosion_rate",
  "am_tool_selection_accuracy",
  "am_parameter_correctness_rate",
  "am_tool_output_validation_rate",
  "am_idempotency_safety_rate",
]);

const TOOL_SPAN_METRIC_NAMES = new Set([
  "tool call success rate",
  "tool call explosion rate",
  "tool selection accuracy",
  "parameter correctness rate",
  "tool output validation rate",
  "idempotency safety rate",
]);

function normalizeMetricName(name) {
  return String(name || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, " ");
}

/** True when this agent metric is scored from the response tool list. */
export function metricNeedsToolCalls(metricId, metricName = "") {
  const id = String(metricId || "").trim();
  if (TOOL_SPAN_METRIC_IDS.has(id)) return true;
  return TOOL_SPAN_METRIC_NAMES.has(normalizeMetricName(metricName));
}

const RAG_CONTEXT_IDS = new Set([
  "rm_context_relevance_score",
  "de_contextual_precision",
  "de_contextual_recall",
  "rm_retrieval_success_rate",
  "rm_context_window_efficiency",
  "rm_corpus_freshness_score",
  "rm_embedding_drift_score",
  "rm_restricted_exposure_rate",
  "rm_staleness_risk_indicator",
  "rm_index_update_latency",
]);

const RAG_GENERATION_IDS = new Set([
  "de_faithfulness",
  "de_answer_relevancy",
  "rm_hallucination_rate",
  "rm_grounded_answer_rate",
  "rm_citation_faithfulness",
  "rm_citation_coverage_rate",
  "rm_context_utilization_rate",
  "rm_answer_completeness_rate",
  "rm_hallucination_severity_index",
  "rm_multi_hop_success_rate",
  "cm_pii_leakage_rate",
  "cm_semantic_similarity_score",
  "cm_factual_accuracy_rate",
  "rm_document_attribution_accuracy",
  "rm_answer_correctness_at_reference",
  "rm_response_consistency_score",
  "rm_indirect_injection_resistance",
]);

const API_ONLY_METRIC_IDS = new Set([
  "am_task_completion_rate",
  "am_decision_audit_trail_coverage",
  "am_plan_accuracy_score",
  "am_goal_alignment_score",
  "am_output_quality_score",
  "am_escalation_trigger_accuracy",
  "am_human_decision_quality_support",
  "am_rationale_faithfulness",
]);

export function normalizeConnectorList(values) {
  const set = new Set();
  for (const raw of values || []) {
    if (Array.isArray(raw)) {
      raw.forEach((v) => {
        const id = String(v || "").trim();
        if (id) set.add(id);
      });
      continue;
    }
    const id = String(raw || "").trim();
    if (id) set.add(id);
  }
  return CONNECTOR_ORDER.filter((id) => set.has(id));
}

function catalogIdOf(item = {}) {
  const explicit = String(item.catalogMetricId || item.formulaFn || item.formula_fn || "").trim();
  if (explicit) return explicit;
  const maybe = String(item.metricId || item.metric_id || "").trim();
  if (/^(am_|rm_|cm_|cp_|de_)/i.test(maybe)) return maybe;
  return "";
}

function metricNameOf(item = {}) {
  return String(item.metricName || item.metric_name || "").trim();
}

function familiesOf(item = {}) {
  return Array.isArray(item.families) ? item.families.map(String) : [];
}

function isRagConnectorMetric(id, families = []) {
  if (id.startsWith("am_") || id.startsWith("cp_")) return false;
  if (id === "cm_semantic_similarity_score") return false;
  if (id && (RAG_CONTEXT_IDS.has(id) || RAG_GENERATION_IDS.has(id))) return true;
  if (id.startsWith("cm_")) return false;
  if (id.startsWith("rm_") || id.startsWith("de_")) return true;
  return (
    families.includes("rag") &&
    !families.includes("agent") &&
    !families.includes("chatbot") &&
    !families.includes("copilot")
  );
}

export function ruleConnectorsForMetricItem(item = {}) {
  const id = catalogIdOf(item);
  const families = familiesOf(item);
  const metricTitle = normalizeMetricName(metricNameOf(item));

  if (
    NO_CONNECTOR_IDS.has(id) ||
    metricTitle === "tevv coverage" ||
    metricTitle === "tevv coverage index"
  ) {
    return [];
  }

  if (isRagConnectorMetric(id, families)) {
    const need = [];
    if (metricNeedsRetrievedContext(id) || RAG_CONTEXT_IDS.has(id)) {
      need.push(CONNECTORS.VECTOR_STORE);
    }
    if (metricNeedsAskApi(id) || RAG_GENERATION_IDS.has(id)) {
      need.push(CONNECTORS.REST_API);
    }
    if (!need.length) need.push(CONNECTORS.VECTOR_STORE);
    if (
      item.implementationTier === "traces" ||
      item.implementation_tier === "traces" ||
      id === "rm_context_window_efficiency"
    ) {
      need.push(CONNECTORS.TRACES);
    }
    return normalizeConnectorList(need);
  }

  if (API_ONLY_METRIC_IDS.has(id) || TOOL_SPAN_METRIC_IDS.has(id)) {
    return normalizeConnectorList([CONNECTORS.REST_API]);
  }

  const traceTier =
    item.implementationTier === "traces" || item.implementation_tier === "traces";

  if (
    id.startsWith("am_") ||
    families.includes("agent") ||
    id.startsWith("cm_") ||
    id.startsWith("cp_") ||
    families.includes("chatbot") ||
    families.includes("copilot")
  ) {
    return normalizeConnectorList(
      traceTier ? [CONNECTORS.REST_API, CONNECTORS.TRACES] : [CONNECTORS.REST_API]
    );
  }

  if (traceTier) {
    return normalizeConnectorList([CONNECTORS.REST_API, CONNECTORS.TRACES]);
  }

  return null;
}

export function connectorsForMetricItem(item = {}) {
  const declared = normalizeConnectorList(item.requiredConnectors || item.required_connectors || []);
  const inferred = ruleConnectorsForMetricItem(item) || [];
  if (!declared.length) return inferred;
  let merged = [...declared];
  if (inferred.includes(CONNECTORS.REST_API) && !merged.includes(CONNECTORS.REST_API)) {
    merged.push(CONNECTORS.REST_API);
  }
  if (inferred.length && !inferred.includes(CONNECTORS.REST_API)) {
    merged = merged.filter((id) => id !== CONNECTORS.REST_API);
  }
  return normalizeConnectorList(merged);
}

export function unionRequiredConnectors(items = []) {
  return normalizeConnectorList(items.flatMap((item) => connectorsForMetricItem(item)));
}

/** Nothing is silently skippable — missing traces would mark agent metrics Not Evaluated. */
export const OPTIONAL_CONNECTORS = Object.freeze([]);

export function isOptionalConnector(id, optionalIds = []) {
  return OPTIONAL_CONNECTORS.includes(id) || normalizeConnectorList(optionalIds).includes(id);
}

function requiredConnectorIds(required, optionalIds = []) {
  return normalizeConnectorList(required).filter((id) => !isOptionalConnector(id, optionalIds));
}

export function connectorsComplete(required, checks = {}, { optionalIds = [] } = {}) {
  const needed = requiredConnectorIds(required, optionalIds);
  if (needed.includes(CONNECTORS.VECTOR_STORE) && !checks.vector) return false;
  if (needed.includes(CONNECTORS.REST_API) && !checks.api) return false;
  if (needed.includes(CONNECTORS.TRACES) && !checks.traces) return false;
  return true;
}

export function connectorVerified(source, complete = true) {
  return Boolean(source?.available) && Boolean(complete);
}

export function activeConnectors(required, checks = {}) {
  const needed = normalizeConnectorList(required);
  return needed.filter((id) => {
    if (id === CONNECTORS.VECTOR_STORE) return Boolean(checks.vector);
    if (id === CONNECTORS.REST_API) return Boolean(checks.api);
    if (id === CONNECTORS.TRACES) return Boolean(checks.traces);
    return false;
  });
}

const CONNECTOR_LABELS = {
  [CONNECTORS.VECTOR_STORE]: "vector database",
  [CONNECTORS.REST_API]: "application API",
  [CONNECTORS.TRACES]: "LangSmith (traces)",
};

export function scopeConnectorsHint(
  required,
  { hasSelection = false, canRun = false, optionalIds = [] } = {}
) {
  const labels = requiredConnectorIds(required, optionalIds)
    .map((id) => CONNECTOR_LABELS[id])
    .filter(Boolean);
  if (!hasSelection) {
    return "";
  }
  if (!labels.length) {
    return "These metrics do not need a customer connection for this run.";
  }
  if (canRun) {
    return "";
  }
  if (labels.length === 1) {
    return `Declare the ${labels[0]} connection in Gate 1 before beginning execution.`;
  }
  const last = labels[labels.length - 1];
  const head = labels.slice(0, -1).join(", ");
  return `Declare the ${head} and ${last} connections in Gate 1 before beginning execution.`;
}
